import { SPELL_BAR_SIZE, spellsOf, } from '../data/spells.js';
import { ringDistance } from '../world/grid.js';
/**
 * Resource, grimoire and spell resolution (ADR 0008, ADR 0048). Pure: the
 * server owns the clock and the state, and asks these functions what a cast
 * may do.
 *
 * The Warrior's resource is energy: a full bar at 100, refilling on its own.
 * A slow refill against costs of 15 to 60 gives about one spell every few
 * seconds — spells punctuate the auto-attack rather than replace it.
 */
export const MAX_ENERGY = 100;
/** Energy regained per second, whatever the character is doing. */
export const ENERGY_PER_SECOND = 6;
/**
 * Energy after `elapsedMs` of refill, capped at the maximum; `perSecond` is
 * the character's, the base raised by its affixes (ADR 0057).
 */
export function regenerate(energy, elapsedMs, perSecond = ENERGY_PER_SECOND) {
    return Math.min(MAX_ENERGY, energy + (perSecond * Math.max(0, elapsedMs)) / 1000);
}
// ---- Grimoire and bar ----
/** Whether a character of `level` who turned in `done` knows the spell. */
export function knowsSpell(spell, level, done) {
    return level >= spell.learn.level && (spell.learn.quest === undefined || done.has(spell.learn.quest));
}
/** The spells a character knows, in the grimoire's order. Derived, never stored. */
export function knownSpells(classId, level, done) {
    return spellsOf(classId)
        .filter((spell) => knowsSpell(spell, level, done))
        .map((spell) => spell.id);
}
/**
 * The bar as it should stand: every saved slot still known kept in place,
 * the rest emptied, then newly learnt spells dropped into the free slots in
 * the grimoire's order, so a spell learnt lands on the bar without a visit to
 * the grimoire. `seen` are the spells the character already knew when the bar
 * was last settled: a spell the player took off on purpose is not put back.
 */
export function settleSpellBar(saved, known, seen) {
    const knownSet = new Set(known);
    const bar = Array.from({ length: SPELL_BAR_SIZE }, (_, index) => {
        const id = saved?.[index] ?? null;
        return id !== null && knownSet.has(id) ? id : null;
    });
    const onBar = new Set(bar.filter((id) => id !== null));
    const seenSet = new Set(seen);
    for (const id of known) {
        if (onBar.has(id) || (saved !== null && seenSet.has(id)))
            continue;
        const free = bar.indexOf(null);
        if (free === -1)
            break;
        bar[free] = id;
        onBar.add(id);
    }
    return bar;
}
/** Whether a bar the player asked for may be kept: its size, known spells, none twice. */
export function checkSpellBar(slots, known) {
    if (slots.length !== SPELL_BAR_SIZE)
        return 'wrong-size';
    const knownSet = new Set(known);
    const seen = new Set();
    for (const id of slots) {
        if (id === null)
            continue;
        if (!knownSet.has(id))
            return 'not-learned';
        if (seen.has(id))
            return 'duplicate';
        seen.add(id);
    }
    return null;
}
/** Whether the cast goes through, and if not, why — in the order a player would want to hear it. */
export function checkCast(spell, context) {
    if (spell.classId !== context.classId)
        return 'wrong-class';
    if (context.known !== undefined && !context.known.includes(spell.id))
        return 'not-learned';
    if (context.bar !== undefined && !context.bar.includes(spell.id))
        return 'not-on-bar';
    if (context.now < context.readyAt)
        return 'cooldown';
    if (context.energy < spell.cost)
        return 'energy';
    if (spell.targeting === 'target') {
        if (context.target === undefined)
            return 'no-target';
        if (ringDistance(context.caster, context.target) > spell.range)
            return 'out-of-range';
    }
    // A wave aimed at a held target must reach it; without one it goes the way the caster faces.
    if (spell.targeting === 'line' &&
        context.target !== undefined &&
        ringDistance(context.caster, context.target) > spell.range)
        return 'out-of-range';
    return null;
}
function damageEffectOf(spell) {
    return spell.effects.find((effect) => effect.kind === 'damage');
}
/** Whether a spell deals damage at all: it then swings, and resets the auto-attack. */
export function isOffensive(spell) {
    return damageEffectOf(spell) !== undefined;
}
/**
 * The multiplier a damage spell applies to the basic hit, given the target's
 * share of health left: an execute's own when the target is low enough. 0
 * for a spell without damage.
 */
export function damageMultiplier(spell, targetHealthShare = 1) {
    const effect = damageEffectOf(spell);
    if (effect === undefined)
        return 0;
    if (effect.execute !== undefined && targetHealthShare <= effect.execute.below) {
        return effect.execute.multiplier;
    }
    return effect.multiplier;
}
/** Damage of a damage spell, from the caster's basic hit; at least one point. */
export function spellDamage(spell, basicHit, targetHealthShare = 1) {
    const multiplier = damageMultiplier(spell, targetHealthShare);
    if (multiplier === 0)
        return 0;
    return Math.max(1, Math.round(basicHit * multiplier));
}
/** Health restored by a heal spell, never past the maximum. */
export function spellHeal(spell, hp, maxHp) {
    const effect = spell.effects.find((candidate) => candidate.kind === 'heal');
    if (effect?.kind !== 'heal')
        return 0;
    const wanted = Math.round(maxHp * effect.maxHealthShare);
    return Math.max(0, Math.min(wanted, maxHp - hp));
}
/** Health a sacrifice takes: its share of the maximum, but always a point left. */
export function spellSacrifice(spell, hp, maxHp) {
    const effect = spell.effects.find((candidate) => candidate.kind === 'sacrifice');
    if (effect?.kind !== 'sacrifice')
        return 0;
    const wanted = Math.round(maxHp * effect.maxHealthShare);
    return Math.max(0, Math.min(wanted, hp - 1));
}
export function hasLeap(spell) {
    return spell.effects.some((effect) => effect.kind === 'leap');
}
/** Tiles within an area spell's radius of the caster. */
export function inArea(spell, caster, tile) {
    return spell.targeting === 'area' && ringDistance(caster, tile) <= spell.range;
}
const FACING_STEP = {
    north: { x: 0, y: -1 },
    south: { x: 0, y: 1 },
    west: { x: -1, y: 0 },
    east: { x: 1, y: 0 },
};
/** Rounds half away from zero, so a line bends the same way on both sides of the caster. */
function roundAway(value) {
    return Math.sign(value) * Math.round(Math.abs(value));
}
/**
 * The tiles a line spell sweeps: `range` of them from the caster, the way
 * they face or straight at a tile they aim for (diagonals included, the
 * aimed tile always on the line when in range), stopping at the first one
 * `blocks` says is a wall so a wave never passes through a cliff.
 */
export function lineTiles(spell, caster, aim, blocks = () => false) {
    if (spell.targeting !== 'line')
        return [];
    const dx = typeof aim === 'string' ? FACING_STEP[aim].x : aim.x - caster.x;
    const dy = typeof aim === 'string' ? FACING_STEP[aim].y : aim.y - caster.y;
    const span = Math.max(Math.abs(dx), Math.abs(dy));
    if (span === 0)
        return [];
    const tiles = [];
    for (let distance = 1; distance <= spell.range; distance += 1) {
        const tile = {
            x: caster.x + roundAway((dx * distance) / span),
            y: caster.y + roundAway((dy * distance) / span),
        };
        if (blocks(tile))
            break;
        tiles.push(tile);
    }
    return tiles;
}
//# sourceMappingURL=spells.js.map